import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Briefcase, Copy, CheckCircle2, Server, UserCheck, Zap, Code2, ShieldAlert, RefreshCw, Terminal, DownloadCloud, UploadCloud, Save, Link, Shield } from "lucide-react";
import { REACT_APP_API_URL } from "config/api-config";

export default function ERPSetup({ onClose }) {
  const [activeTab, setActiveTab] = useState("push"); // "push" or "fetch"
  
  // --- Push API State (Customer -> CamSec) ---
  const [apiKey, setApiKey] = useState("Loading...");
  const [allowedIps, setAllowedIps] = useState("");
  const [savingPush, setSavingPush] = useState(false);
  const [resettingKey, setResettingKey] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);

  // --- Fetch API State (CamSec -> Customer) ---
  const [externalUrl, setExternalUrl] = useState("");
  const [externalToken, setExternalToken] = useState("");
  const [savingFetch, setSavingFetch] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);

  const getDynamicApiUrl = () => {
    const currentHost = window.location.hostname;
    const endpoint = "/events/erp/sync_user";
    if (currentHost === "localhost" || currentHost === "127.0.0.1") return `${REACT_APP_API_URL}${endpoint}`;
    const baseUrl = REACT_APP_API_URL.includes("/api") ? `${window.location.origin}/api` : window.location.origin;
    return `${baseUrl}${endpoint}`;
  };

  const pushApiUrl = getDynamicApiUrl();

  useEffect(() => {
    fetchPushSettings();
    fetchExternalSettings();
  }, []);

  const fetchPushSettings = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${REACT_APP_API_URL}/events/erp/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(res.data.api_key);
      setAllowedIps(res.data.allowed_ips);
    } catch (error) { toast.error("Failed to load Push ERP settings."); }
  };

  const fetchExternalSettings = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${REACT_APP_API_URL}/events/erp/external_config`, { headers: { Authorization: `Bearer ${token}` } });
      setExternalUrl(res.data.url);
      setExternalToken(res.data.token);
    } catch (error) { }
  };

  const handleSavePushSecurity = async () => {
    setSavingPush(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/erp/settings`, { allowed_ips: allowedIps }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Security settings saved!");
    } catch (error) { toast.error("Failed to save settings."); } finally { setSavingPush(false); }
  };

  const handleResetPushKey = async () => {
    if(!window.confirm("Are you sure? This will instantly break your current ERP integration until you update your software with the new key.")) return;
    setResettingKey(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/erp/settings/reset_key`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setApiKey(res.data.api_key); 
      toast.success("Old key destroyed! New key generated.");
    } catch (error) { toast.error("Failed to reset API key."); } finally { setResettingKey(false); }
  };

  const handleSaveExternalConfig = async () => {
    setSavingFetch(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/erp/external_config`, { url: externalUrl, token: externalToken }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("External ERP details saved!");
    } catch (error) { toast.error("Failed to save external config."); } finally { setSavingFetch(false); }
  };

  const handleManualFetch = async () => {
    setFetchingData(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/erp/fetch_from_external`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Successfully imported ${res.data.synced_count} profiles!`);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to fetch from ERP."); } finally { setFetchingData(false); }
  };

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const jsonPayload = `{
  "user_type": "Student",
  "user_id": "STU-9001",
  "first_name": "Aryan",
  "last_name": "Sharma",
  "email": "aryan@example.com",
  "mobile_no": "9876543210",
  "gender": "Male",
  "membership_plan": "Yearly",
  "start_date": "2026-08-01",
  "end_date": "2027-05-30"
}`;

  const curlCommand = `curl -X POST ${pushApiUrl} \\
-H "Content-Type: application/json" \\
-H "x-api-key: ${apiKey}" \\
-d '{"user_type": "Student", "user_id": "STU-9001", "first_name": "Aryan", "last_name": "Sharma", "email": "aryan@example.com", "mobile_no": "9876543210", "gender": "Male", "membership_plan": "Yearly", "start_date": "2026-08-01", "end_date": "2027-05-30"}'`;

  return (
    <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 25px rgba(0,0,0,0.05)", maxHeight: "90vh", overflowY: "auto" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "25px", borderBottom: "1px solid #F4F7FE", paddingBottom: "20px" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "14px", backgroundColor: "#fff4eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Briefcase size={28} color="#FF912C" />
        </div>
        <div>
          <h3 style={{ margin: 0, color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>School ERP / HRMS Integration</h3>
          <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Automate your student and staff entries across platforms.</p>
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", backgroundColor: "#FAFCFE", padding: "0 30px", margin: "-30px -30px 25px -30px" }}>
        <button 
          onClick={() => setActiveTab("push")}
          style={{ padding: "16px 20px", background: "none", border: "none", borderBottom: activeTab === "push" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "push" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <UploadCloud size={18} /> Option 1: Push API (They send to us)
        </button>
        <button 
          onClick={() => setActiveTab("fetch")}
          style={{ padding: "16px 20px", background: "none", border: "none", borderBottom: activeTab === "fetch" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "fetch" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <DownloadCloud size={18} /> Option 2: Fetch API (We pull from them)
        </button>
      </div>

      {/* === OPTION 1: PUSH API === */}
      {activeTab === "push" && (
        <div style={{ animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ marginBottom: "25px", backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #4318FF" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>How the Push API Works</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Give the URL and API Key below to your IT Developer. Their software will send a <code>POST</code> request to CamSec Ai whenever a new Student or Staff profile is created in their system.
            </p>
          </div>

          <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: "20px", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldAlert size={18} color="#EE5D50" /> Security Configuration
            </h4>

            <div style={{ marginBottom: "15px" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>YOUR SECRET API KEY</p>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <input type="text" readOnly value={apiKey} style={{ flexGrow: 1, minWidth: "250px", padding: "10px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", backgroundColor: "#F8FAFC", color: "#EE5D50", fontWeight: "600", letterSpacing: "1px" }} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => copyToClipboard(apiKey, setCopiedKey)} style={{ padding: "10px 16px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>{copiedKey ? "Copied" : "Copy"}</button>
                  <button onClick={handleResetPushKey} disabled={resettingKey} style={{ padding: "10px 16px", backgroundColor: "#ffeeec", color: "#EE5D50", border: "none", borderRadius: "8px", cursor: resettingKey ? "not-allowed" : "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                    <RefreshCw size={16} className={resettingKey ? "animate-spin" : ""} /> {resettingKey ? "Resetting..." : "Reset Key"}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>WHITELISTED IP ADDRESSES (OPTIONAL)</p>
              <input type="text" placeholder="e.g. 203.0.113.45 (Leave blank to allow all IPs)" value={allowedIps} onChange={(e) => setAllowedIps(e.target.value)} style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", outline: "none", color: "#2B3674" }} />
            </div>
            <button onClick={handleSavePushSecurity} disabled={savingPush} style={{ padding: "8px 16px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>{savingPush ? "Saving..." : "Save Security Rules"}</button>
          </div>

          <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>For Your IT Developer:</h4>

            <div style={{ backgroundColor: "#F8FAFC", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0", marginBottom: "15px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#A3AED0", fontWeight: "700" }}>WEBHOOK ENDPOINT URL</p>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <code style={{ flexGrow: 1, color: "#4318FF", fontSize: "14px", fontWeight: "600" }}>{pushApiUrl}</code>
                <button onClick={() => copyToClipboard(pushApiUrl, setCopiedUrl)} style={{ padding: "8px 12px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedUrl ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>

            <div style={{ backgroundColor: "#0B1437", padding: "16px", borderRadius: "12px", marginBottom: "15px", border: "1px solid #2B3674" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}><Terminal size={14} /> TEST IT NOW (cURL)</p>
                <button onClick={() => copyToClipboard(curlCommand, setCopiedCurl)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedCurl ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copiedCurl ? "COPIED" : "COPY CMD"}
                </button>
              </div>
              <pre style={{ margin: 0, padding: "12px", backgroundColor: "#111C44", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto" }}>{curlCommand}</pre>
            </div>

            <div style={{ backgroundColor: "#111C44", padding: "16px", borderRadius: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}><Code2 size={14} /> EXAMPLE JSON FORMAT</p>
                <button onClick={() => copyToClipboard(jsonPayload, setCopiedJson)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedJson ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copiedJson ? "COPIED" : "COPY JSON"}
                </button>
              </div>
              <pre style={{ margin: 0, padding: "15px", backgroundColor: "#0B1437", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto", border: "1px solid #2B3674" }}>{jsonPayload}</pre>
            </div>
          </div>
        </div>
      )}

      {/* === OPTION 2: FETCH API === */}
      {activeTab === "fetch" && (
        <div style={{ animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ marginBottom: "25px", backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #05CD99" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>How the Fetch API Works</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Instead of the customer's software pushing data to us, <b>we pull it from them</b>. Provide their API URL and Token below, and you can instantly fetch and synchronize their user database into CamSec Ai.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "15px", marginBottom: "20px" }}>
            <div>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                <Link size={16} color="#05CD99"/> Customer's API URL
              </label>
              <input 
                type="text" 
                placeholder="https://their-school-erp.com/api/get_users"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", boxSizing: "border-box", backgroundColor: "#F8FAFC" }}
              />
            </div>

            <div>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                <Shield size={16} color="#05CD99"/> API Token / Authorization (Optional)
              </label>
              <input 
                type="password" 
                placeholder="Bearer Token required to access their API"
                value={externalToken}
                onChange={(e) => setExternalToken(e.target.value)}
                style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", boxSizing: "border-box", backgroundColor: "#F8FAFC" }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "15px" }}>
            <button 
              onClick={handleManualFetch}
              disabled={fetchingData || !externalUrl}
              style={{ flex: 1, padding: "14px", backgroundColor: (!externalUrl || fetchingData) ? "#F4F7FE" : "#e6fbf4", color: (!externalUrl || fetchingData) ? "#A3AED0" : "#05CD99", border: (!externalUrl || fetchingData) ? "1px solid #E2E8F0" : "1px solid #05CD99", borderRadius: "12px", fontWeight: "700", cursor: (!externalUrl || fetchingData) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "0.2s" }}
            >
              <RefreshCw size={18} className={fetchingData ? "animate-spin" : ""} />
              {fetchingData ? "Fetching from ERP..." : "Fetch & Sync Now"}
            </button>
            
            <button 
              onClick={handleSaveExternalConfig}
              disabled={savingFetch || !externalUrl}
              style={{ flex: 1, padding: "14px", backgroundColor: (!externalUrl || savingFetch) ? "#A3AED0" : "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: (!externalUrl || savingFetch) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "0.2s" }}
            >
              <Save size={18} />
              {savingFetch ? "Saving..." : "Save External ERP Config"}
            </button>
          </div>

          <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "#F0EBFF", borderRadius: "12px", border: "1px solid #D4C4FF" }}>
            <h4 style={{ margin: "0 0 6px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>Required JSON Response Format</h4>
            <p style={{ margin: 0, color: "#475467", fontSize: "13px", lineHeight: "1.5" }}>
              When CamSec Ai calls the customer's URL, their API must return an array of users formatted like this:
            </p>
            <pre style={{ margin: "10px 0 0 0", padding: "10px", backgroundColor: "#FFFFFF", border: "1px solid #D4C4FF", borderRadius: "8px", color: "#4318FF", fontSize: "12px", fontFamily: "monospace", overflowX: "auto" }}>
[
  {'{'}
    "user_type": "Student",
    "user_id": "STU-9001",
    "first_name": "Aryan",
    "last_name": "Sharma",
    "email": "aryan@example.com",
    "mobile_no": "9876543210",
    "gender": "Male",
    "membership_plan": "Yearly",
    "start_date": "2026-08-01",
    "end_date": "2027-05-30"
  {'}'}
]
            </pre>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #F4F7FE", paddingTop: "20px", marginTop: "20px" }}>
        <button onClick={onClose} style={{ padding: "12px 24px", backgroundColor: "#F4F7FE", color: "#A3AED0", border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
          Close Panel
        </button>
      </div>
    </div>
  );
}
